import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, LayoutDashboard, FileText, Users, Star, Phone, Inbox,
  Save, Loader2, Plus, Trash2, RefreshCw, CheckCircle, Mail, Building2, Clock,
  ChevronDown, ChevronUp, Eye, ThumbsUp, ThumbsDown, MessageSquareQuote, Link
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { brand } from "../mock";

const API = (path, opts = {}) => {
  const token = localStorage.getItem("rsp_token");
  return fetch(`/api${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
  });
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("submissions");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("rsp_token");
    if (!token) { nav("/admin/login"); return; }
    API("/auth/me").then((r) => { if (!r.ok) { localStorage.removeItem("rsp_token"); nav("/admin/login"); } });
  }, [nav]);

  const logout = () => { localStorage.removeItem("rsp_token"); nav("/admin/login"); };

  const save = async (section, data) => {
    setSaving(true); setMsg("");
    try {
      const r = await API(`/content/${section}`, { method: "PUT", body: JSON.stringify({ data }) });
      if (!r.ok) throw new Error("Save failed");
      setMsg("Saved successfully!");
    } catch (e) { setMsg("Error: " + e.message); }
    finally { setSaving(false); setTimeout(() => setMsg(""), 3000); }
  };

  const tabs = [
    { id: "submissions", label: "Inbox", icon: Inbox },
    { id: "reviews", label: "Reviews", icon: MessageSquareQuote },
    { id: "hero", label: "Hero", icon: LayoutDashboard },
    { id: "services", label: "Services", icon: FileText },
    { id: "clients", label: "Clients", icon: Users },
    { id: "testimonials", label: "Testimonials", icon: Star },
    { id: "contact_info", label: "Contact Info", icon: Phone },
  ];

  return (
    <div className="min-h-screen bg-[#faf9f9] flex flex-col">
      {/* Top bar */}
      <header className="bg-white border-b border-[#E5E5E5] h-16 flex items-center px-6 gap-4 sticky top-0 z-40">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-white grid place-items-center border border-[#E5E5E5] shadow-sm overflow-hidden">
            <img src={brand.logo} alt="RSP" className="w-7 h-7 object-contain" />
          </div>
          <span className="text-sm font-semibold text-[#111]">RSP<span className="text-[#73100D]">Innov</span> Admin</span>
        </div>
        {msg && (
          <div className={`ml-4 text-xs font-medium px-3 py-1 rounded-full ${msg.startsWith("Error") ? "bg-red-50 text-red-600" : "bg-green-50 text-green-700"}`}>
            {msg}
          </div>
        )}
        <div className="ml-auto flex items-center gap-3">
          <a href="/" target="_blank" rel="noreferrer" className="text-xs text-[#666] hover:text-[#73100D] flex items-center gap-1">
            <Eye className="w-3.5 h-3.5" /> View site
          </a>
          <button onClick={logout} className="inline-flex items-center gap-1.5 text-xs text-[#666] hover:text-[#73100D]">
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar */}
        <aside className="w-48 bg-white border-r border-[#E5E5E5] flex-shrink-0 hidden md:block">
          <nav className="p-3 space-y-0.5">
            {tabs.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    tab === t.id ? "bg-[#73100D]/8 text-[#73100D]" : "text-[#555] hover:bg-[#f5f5f5]"
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {t.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Mobile tab bar */}
        <div className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-[#E5E5E5] flex z-40">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex-1 flex flex-col items-center py-2 text-[9px] gap-0.5 ${tab === t.id ? "text-[#73100D]" : "text-[#999]"}`}>
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Main */}
        <main className="flex-1 p-6 pb-20 md:pb-6 overflow-y-auto">
          {tab === "submissions" && <SubmissionsTab />}
          {tab === "reviews" && <ReviewsTab />}
          {tab === "hero" && <HeroTab onSave={save} saving={saving} />}
          {tab === "services" && <ServicesTab onSave={save} saving={saving} />}
          {tab === "clients" && <ClientsTab onSave={save} saving={saving} />}
          {tab === "testimonials" && <TestimonialsTab onSave={save} saving={saving} />}
          {tab === "contact_info" && <ContactInfoTab onSave={save} saving={saving} />}
        </main>
      </div>
    </div>
  );
}

// ─── Reusable ────────────────────────────────────────────────────────────────
function SectionHeader({ title, children }) {
  return (
    <div className="flex items-center justify-between mb-6">
      <h2 className="text-lg font-semibold text-[#111]">{title}</h2>
      {children}
    </div>
  );
}

function SaveBtn({ saving, onClick }) {
  return (
    <button onClick={onClick} disabled={saving}
      className="inline-flex items-center gap-2 px-5 h-9 rounded-full bg-[#73100D] text-white text-sm font-semibold hover:bg-[#5E0000] transition-colors disabled:opacity-60">
      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
      Save
    </button>
  );
}

function Card({ children, className = "" }) {
  return <div className={`bg-white rounded-2xl border border-[#E5E5E5] p-6 ${className}`}>{children}</div>;
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-[0.15em] text-[#666] mb-1.5 font-semibold">{label}</div>
      {children}
    </label>
  );
}

function useSection(section) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API(`/content/${section}`);
      const json = await r.json();
      setData(json.data);
    } catch { } finally { setLoading(false); }
  }, [section]);
  useEffect(() => { load(); }, [load]);
  return [data, setData, loading];
}

// ─── Submissions ─────────────────────────────────────────────────────────────
function SubmissionsTab() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await API("/submissions");
      setSubs(await r.json());
    } catch { } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    await API(`/submissions/${id}/read`, { method: "PUT" });
    setSubs((s) => s.map((x) => x.id === id ? { ...x, read: true } : x));
  };
  const del = async (id) => {
    await API(`/submissions/${id}`, { method: "DELETE" });
    setSubs((s) => s.filter((x) => x.id !== id));
  };

  const unread = subs.filter((s) => !s.read).length;

  return (
    <div>
      <SectionHeader title={`Inbox ${unread > 0 ? `(${unread} unread)` : ""}`}>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-xs text-[#666] hover:text-[#73100D]">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </SectionHeader>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#73100D]" /></div>
      ) : subs.length === 0 ? (
        <Card className="text-center py-16 text-[#999] text-sm">No submissions yet.</Card>
      ) : (
        <div className="space-y-3">
          {subs.map((s) => (
            <Card key={s.id} className={`transition-all ${!s.read ? "border-[#73100D]/30 bg-[#fff]" : "bg-[#fafafa]"}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {!s.read && <span className="w-2 h-2 rounded-full bg-[#73100D] flex-shrink-0" />}
                    <span className="font-semibold text-[#111] text-sm">{s.name}</span>
                    {s.company && <span className="text-xs text-[#888] flex items-center gap-1"><Building2 className="w-3 h-3" />{s.company}</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-[#666]">
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{s.email}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(s.submitted_at).toLocaleString()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {!s.read && (
                    <button onClick={() => markRead(s.id)} title="Mark read"
                      className="text-[#73100D] hover:text-[#5E0000]"><CheckCircle className="w-4 h-4" /></button>
                  )}
                  <button onClick={() => setExpanded(expanded === s.id ? null : s.id)}
                    className="text-[#999] hover:text-[#333]">
                    {expanded === s.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  <button onClick={() => del(s.id)} title="Delete"
                    className="text-[#ccc] hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {expanded === s.id && (
                <div className="mt-3 pt-3 border-t border-[#E5E5E5] text-sm text-[#333] leading-relaxed whitespace-pre-wrap">
                  {s.message}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
function HeroTab({ onSave, saving }) {
  const [data, setData, loading] = useSection("hero");

  const set = (key, val) => setData((d) => ({ ...d, [key]: val }));
  const setTrust = (i, k, val) => setData((d) => {
    const t = [...d.trust]; t[i] = { ...t[i], [k]: val }; return { ...d, trust: t };
  });
  const setRotating = (i, val) => setData((d) => {
    const r = [...d.rotating]; r[i] = val; return { ...d, rotating: r };
  });

  if (loading || !data) return <Loader2 className="w-5 h-5 animate-spin text-[#73100D] mx-auto mt-16" />;

  return (
    <div>
      <SectionHeader title="Hero Section">
        <SaveBtn saving={saving} onClick={() => onSave("hero", data)} />
      </SectionHeader>
      <div className="space-y-5">
        <Card>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Tagline">
              <Input value={data.tagline} onChange={(e) => set("tagline", e.target.value)} className="h-10 border-[#E5E5E5]" />
            </Field>
            <Field label="Primary CTA Button">
              <Input value={data.primaryCta} onChange={(e) => set("primaryCta", e.target.value)} className="h-10 border-[#E5E5E5]" />
            </Field>
            <Field label="Title">
              <Input value={data.title} onChange={(e) => set("title", e.target.value)} className="h-10 border-[#E5E5E5]" />
            </Field>
            <Field label="Highlight (bold)">
              <Input value={data.highlight} onChange={(e) => set("highlight", e.target.value)} className="h-10 border-[#E5E5E5]" />
            </Field>
          </div>
          <div className="mt-5">
            <Field label="Description">
              <Textarea value={data.description} onChange={(e) => set("description", e.target.value)} rows={3} className="border-[#E5E5E5]" />
            </Field>
          </div>
        </Card>

        <Card>
          <div className="text-xs uppercase tracking-[0.15em] text-[#666] mb-3 font-semibold">Rotating Specialisms</div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {(data.rotating || []).map((r, i) => (
              <Input key={i} value={r} onChange={(e) => setRotating(i, e.target.value)} className="h-10 border-[#E5E5E5]" />
            ))}
          </div>
        </Card>

        <Card>
          <div className="text-xs uppercase tracking-[0.15em] text-[#666] mb-3 font-semibold">Trust Stats</div>
          <div className="grid sm:grid-cols-3 gap-4">
            {(data.trust || []).map((t, i) => (
              <div key={i} className="space-y-2">
                <Input placeholder="Value e.g. 85+" value={t.k} onChange={(e) => setTrust(i, "k", e.target.value)} className="h-10 border-[#E5E5E5]" />
                <Input placeholder="Label e.g. Enterprise Clients" value={t.v} onChange={(e) => setTrust(i, "v", e.target.value)} className="h-10 border-[#E5E5E5]" />
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ─── Services ────────────────────────────────────────────────────────────────
function ServicesTab({ onSave, saving }) {
  const [data, setData, loading] = useSection("services");

  const update = (i, key, val) => setData((d) => {
    const arr = [...d]; arr[i] = { ...arr[i], [key]: val }; return arr;
  });
  const remove = (i) => setData((d) => d.filter((_, idx) => idx !== i));
  const add = () => setData((d) => [...d, { id: Date.now(), icon: "Bot", title: "", desc: "" }]);

  if (loading || !data) return <Loader2 className="w-5 h-5 animate-spin text-[#73100D] mx-auto mt-16" />;

  return (
    <div>
      <SectionHeader title="Services">
        <div className="flex items-center gap-2">
          <button onClick={add} className="inline-flex items-center gap-1.5 text-xs text-[#73100D] font-medium hover:underline">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
          <SaveBtn saving={saving} onClick={() => onSave("services", data)} />
        </div>
      </SectionHeader>
      <div className="space-y-3">
        {data.map((svc, i) => (
          <Card key={svc.id || i}>
            <div className="flex items-start gap-4">
              <div className="flex-1 grid sm:grid-cols-3 gap-3">
                <Field label="Icon name">
                  <Input value={svc.icon} onChange={(e) => update(i, "icon", e.target.value)} className="h-10 border-[#E5E5E5]" />
                </Field>
                <Field label="Title">
                  <Input value={svc.title} onChange={(e) => update(i, "title", e.target.value)} className="h-10 border-[#E5E5E5]" />
                </Field>
                <Field label="Description">
                  <Input value={svc.desc} onChange={(e) => update(i, "desc", e.target.value)} className="h-10 border-[#E5E5E5]" />
                </Field>
              </div>
              <button onClick={() => remove(i)} className="mt-6 text-[#ccc] hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─── Clients ─────────────────────────────────────────────────────────────────
function ClientsTab({ onSave, saving }) {
  const [data, setData, loading] = useSection("clients");

  const normalize = (c) => typeof c === "string" ? { name: c, logo: "", region: "domestic" } : { logo: "", region: "domestic", ...c };
  const update = (i, key, val) => setData((d) => { const a = d.map(normalize); a[i] = { ...a[i], [key]: val }; return a; });
  const remove = (i) => setData((d) => d.filter((_, idx) => idx !== i));
  const add = () => setData((d) => [...d, { name: "", logo: "", region: "domestic" }]);

  if (loading || !data) return <Loader2 className="w-5 h-5 animate-spin text-[#73100D] mx-auto mt-16" />;

  const list = (Array.isArray(data) ? data : []).map(normalize);

  return (
    <div>
      <SectionHeader title="Clients">
        <div className="flex items-center gap-2">
          <button onClick={add} className="inline-flex items-center gap-1.5 text-xs text-[#73100D] font-medium hover:underline">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
          <SaveBtn saving={saving} onClick={() => onSave("clients", list)} />
        </div>
      </SectionHeader>
      <div className="space-y-3">
        {list.map((c, i) => (
          <Card key={i}>
            <div className="flex items-center gap-3">
              {/* Logo preview */}
              <div className="w-12 h-12 rounded-xl border border-[#E5E5E5] bg-[#fafafa] grid place-items-center shrink-0 overflow-hidden">
                {c.logo ? (
                  <img src={c.logo} alt="" className="w-full h-full object-contain p-1" onError={(e) => e.target.style.display='none'} />
                ) : (
                  <span className="text-xs font-bold text-[#ccc]">{(c.name || "?")[0]?.toUpperCase()}</span>
                )}
              </div>
              <div className="flex-1 grid sm:grid-cols-3 gap-3">
                <Field label="Client Name">
                  <Input value={c.name} onChange={(e) => update(i, "name", e.target.value)} placeholder="Wipro" className="h-10 border-[#E5E5E5]" />
                </Field>
                <Field label="Logo URL">
                  <Input value={c.logo} onChange={(e) => update(i, "logo", e.target.value)} placeholder="https://..." className="h-10 border-[#E5E5E5]" />
                </Field>
                <Field label="Region">
                  <select value={c.region} onChange={(e) => update(i, "region", e.target.value)}
                    className="w-full h-10 border border-[#E5E5E5] rounded-md px-3 text-sm text-[#333] bg-white focus:outline-none focus:ring-2 focus:ring-[#73100D]/20">
                    <option value="domestic">Domestic</option>
                    <option value="global">Global</option>
                  </select>
                </Field>
              </div>
              <button onClick={() => remove(i)} className="text-[#ccc] hover:text-red-500 flex-shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─── Testimonials ─────────────────────────────────────────────────────────────
function TestimonialsTab({ onSave, saving }) {
  const [data, setData, loading] = useSection("testimonials");

  const update = (i, key, val) => setData((d) => { const a = [...d]; a[i] = { ...a[i], [key]: val }; return a; });
  const remove = (i) => setData((d) => d.filter((_, idx) => idx !== i));
  const add = () => setData((d) => [...d, { id: Date.now(), name: "", role: "", avatar: "", text: "" }]);

  if (loading || !data) return <Loader2 className="w-5 h-5 animate-spin text-[#73100D] mx-auto mt-16" />;

  return (
    <div>
      <SectionHeader title="Testimonials">
        <div className="flex items-center gap-2">
          <button onClick={add} className="inline-flex items-center gap-1.5 text-xs text-[#73100D] font-medium hover:underline">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
          <SaveBtn saving={saving} onClick={() => onSave("testimonials", data)} />
        </div>
      </SectionHeader>
      <div className="space-y-4">
        {data.map((t, i) => (
          <Card key={t.id || i}>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name">
                <Input value={t.name} onChange={(e) => update(i, "name", e.target.value)} className="h-10 border-[#E5E5E5]" />
              </Field>
              <Field label="Role / Company">
                <Input value={t.role} onChange={(e) => update(i, "role", e.target.value)} className="h-10 border-[#E5E5E5]" />
              </Field>
            </div>
            <div className="mt-3">
              <Field label="Testimonial">
                <Textarea value={t.text} onChange={(e) => update(i, "text", e.target.value)} rows={3} className="border-[#E5E5E5]" />
              </Field>
            </div>
            <div className="mt-2 flex justify-end">
              <button onClick={() => remove(i)} className="text-xs text-[#ccc] hover:text-red-500 flex items-center gap-1">
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─── Reviews (pending testimonials) ──────────────────────────────────────────
function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await API("/testimonials/pending"); setReviews(await r.json()); }
    catch { } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    setActing(id + "_approve");
    await API(`/testimonials/${id}/approve`, { method: "PUT" });
    setReviews((r) => r.filter((x) => x.id !== id));
    setActing(null);
  };
  const reject = async (id) => {
    setActing(id + "_reject");
    await API(`/testimonials/${id}/reject`, { method: "PUT" });
    setReviews((r) => r.filter((x) => x.id !== id));
    setActing(null);
  };
  const del = async (id) => {
    await API(`/testimonials/${id}`, { method: "DELETE" });
    setReviews((r) => r.filter((x) => x.id !== id));
  };

  const shareLink = `${window.location.origin}/testimonial`;

  return (
    <div>
      <SectionHeader title="Pending Testimonials">
        <button onClick={load} className="inline-flex items-center gap-1.5 text-xs text-[#666] hover:text-[#73100D]">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </SectionHeader>

      {/* Share link card */}
      <Card className="mb-5 bg-[#73100D]/4 border-[#73100D]/20">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-sm font-semibold text-[#111] flex items-center gap-2">
              <Link className="w-4 h-4 text-[#73100D]" /> Public testimonial form link
            </div>
            <div className="text-xs text-[#666] mt-0.5">Share this link with clients to collect testimonials</div>
          </div>
          <div className="flex items-center gap-2">
            <code className="text-xs bg-white border border-[#E5E5E5] rounded-lg px-3 py-1.5 text-[#333] select-all">{shareLink}</code>
            <button onClick={() => navigator.clipboard.writeText(shareLink)}
              className="text-xs text-[#73100D] font-medium border border-[#73100D]/30 rounded-lg px-3 py-1.5 hover:bg-[#73100D]/5 transition-colors">
              Copy
            </button>
            <a href={shareLink} target="_blank" rel="noreferrer"
              className="text-xs text-[#666] border border-[#E5E5E5] rounded-lg px-3 py-1.5 hover:bg-[#f5f5f5] transition-colors flex items-center gap-1">
              <Eye className="w-3.5 h-3.5" /> Preview
            </a>
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#73100D]" /></div>
      ) : reviews.length === 0 ? (
        <Card className="text-center py-16 text-[#999] text-sm">No pending testimonials. Share the link above to collect reviews.</Card>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <Card key={r.id}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#111] text-sm">{r.name}</span>
                    {r.company && <span className="text-xs text-[#888] flex items-center gap-1"><Building2 className="w-3 h-3" />{r.company}</span>}
                    {r.role && r.role !== r.company && <span className="text-xs text-[#aaa]">· {r.role}</span>}
                  </div>
                  <div className="text-xs text-[#888] mt-0.5 flex items-center gap-1">
                    <Clock className="w-3 h-3" />{new Date(r.submitted_at).toLocaleString()}
                  </div>
                  <p className="mt-3 text-sm text-[#444] leading-relaxed italic">"{r.text}"</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => approve(r.id)} disabled={!!acting}
                    className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-green-50 border border-green-200 text-green-700 text-xs font-semibold hover:bg-green-100 transition-colors disabled:opacity-50">
                    {acting === r.id + "_approve" ? <Loader2 className="w-3 h-3 animate-spin" /> : <ThumbsUp className="w-3 h-3" />}
                    Approve
                  </button>
                  <button onClick={() => reject(r.id)} disabled={!!acting}
                    className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-xs font-semibold hover:bg-orange-100 transition-colors disabled:opacity-50">
                    {acting === r.id + "_reject" ? <Loader2 className="w-3 h-3 animate-spin" /> : <ThumbsDown className="w-3 h-3" />}
                    Reject
                  </button>
                  <button onClick={() => del(r.id)} className="text-[#ccc] hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Contact Info ─────────────────────────────────────────────────────────────
function ContactInfoTab({ onSave, saving }) {
  const [data, setData, loading] = useSection("contact_info");

  const set = (key, val) => setData((d) => ({ ...d, [key]: val }));

  if (loading || !data) return <Loader2 className="w-5 h-5 animate-spin text-[#73100D] mx-auto mt-16" />;

  return (
    <div>
      <SectionHeader title="Contact Information">
        <SaveBtn saving={saving} onClick={() => onSave("contact_info", data)} />
      </SectionHeader>
      <Card>
        <div className="space-y-4">
          <Field label="Email">
            <Input value={data.email || ""} onChange={(e) => set("email", e.target.value)} className="h-10 border-[#E5E5E5]" />
          </Field>
          <Field label="Phone">
            <Input value={data.phone || ""} onChange={(e) => set("phone", e.target.value)} className="h-10 border-[#E5E5E5]" />
          </Field>
          <Field label="Website">
            <Input value={data.website || ""} onChange={(e) => set("website", e.target.value)} className="h-10 border-[#E5E5E5]" />
          </Field>
          <Field label="Address">
            <Textarea value={data.address || ""} onChange={(e) => set("address", e.target.value)} rows={3} className="border-[#E5E5E5]" />
          </Field>
        </div>
      </Card>
    </div>
  );
}
